<template>
  <div class="dri">
    <p class="dri-help">
      Личный кабинет ВятГУ показывает только текущие недели. Прошедшие занятия можно восстановить из
      расписания кафедры («Занятость преподавателей» на vyatsu.ru). Уже имеющиеся у вас занятия
      не изменяются — добавляются только те, которых нет.
    </p>

    <b-overlay :show="loading" rounded="sm">
      <div class="dri-fields">
        <div class="dri-field">
          <label class="dri-label" for="dri-kaf">Кафедра</label>
          <b-form-select id="dri-kaf" v-model="kafId" :options="kafOptions" @change="kafChanged">
            <template #first><b-form-select-option :value="''" disabled>Выберите кафедру</b-form-select-option></template>
          </b-form-select>
        </div>
        <div class="dri-field">
          <label class="dri-label" for="dri-week">Неделя</label>
          <b-form-select id="dri-week" v-model="week" :options="weekOptions" :disabled="!kafId || !weeks.length" @change="weekChanged">
            <template #first><b-form-select-option :value="''" disabled>{{ kafId && !weeks.length ? 'Нет расписания' : 'Выберите неделю' }}</b-form-select-option></template>
          </b-form-select>
        </div>
        <div class="dri-field">
          <label class="dri-label" for="dri-teacher">Преподаватель</label>
          <b-form-select id="dri-teacher" v-model="teacher" :options="teachers" :disabled="!week || !teachers.length" @change="preview">
            <template #first><b-form-select-option :value="''" disabled>Выберите себя в списке</b-form-select-option></template>
          </b-form-select>
        </div>
      </div>

      <b-alert v-if="error" show variant="danger" class="mt-3 mb-0">{{ error }}</b-alert>

      <template v-if="items">
        <p v-if="!items.length" class="text-muted mt-3 mb-0">У преподавателя нет занятий на этой неделе в расписании кафедры.</p>
        <template v-else>
          <div class="dri-summary">
            <span class="dri-badge dri-new">новых: {{ counts.new }}</span>
            <span class="dri-badge dri-exists">уже есть: {{ counts.exists }}</span>
            <span v-if="counts.collision" class="dri-badge dri-collision">коллизий: {{ counts.collision }}</span>
          </div>
          <b-alert v-if="counts.collision" show variant="danger" class="dri-collision-alert">
            <b>Коллизии: {{ counts.collision }}.</b> В эти пары у вас уже стоит другое занятие. Оно останется как есть,
            занятие кафедры добавлено не будет. При необходимости поправьте вручную.
          </b-alert>

          <ul class="dri-list">
            <li v-for="it in items" :key="it.key" class="dri-item" :class="'dri-st-' + it.status">
              <label class="dri-check">
                <input v-if="it.status === 'new'" v-model="selected" type="checkbox" :value="it.key"
                  :aria-label="'Добавить ' + it.predm + ' ' + it.day + ' ' + it.time">
              </label>
              <div class="dri-body">
                <div class="dri-when">{{ dayLabel(it.day) }} · {{ it.time }}
                  <span class="dri-status">{{ STATUS[it.status] }}</span>
                </div>
                <div class="dri-what"><b>{{ it.predm }}</b> <span class="text-muted">{{ it.type }}</span></div>
                <div class="dri-meta">{{ it.kab }} {{ it.groups.join(', ') }}<span v-if="it.subgroups" class="dri-sub"> · {{ it.subgroups }}</span></div>
                <div v-if="it.status === 'collision'" class="dri-local">
                  У вас: <b>{{ it.local.predm }}</b> {{ it.local.type }} {{ (it.local.groups || []).join(', ') }}
                </div>
              </div>
            </li>
          </ul>

          <div class="dri-actions">
            <b-button size="sm" variant="outline-primary" :disabled="!newKeys.length" @click="toggleAll">
              {{ selected.length === newKeys.length ? 'Снять все' : 'Выбрать все' }}
            </b-button>
            <b-button variant="primary" :disabled="importing || !selected.length" @click="doImport">
              <b-spinner v-if="importing" small></b-spinner>
              Добавить {{ selected.length }} {{ plural(selected.length) }}
            </b-button>
          </div>
        </template>
      </template>
    </b-overlay>
  </div>
</template>

<script>
const PATH = process.env.VUE_APP_PATH
const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']
const STATUS = { new: 'новое', exists: 'уже есть', collision: 'коллизия' }

// последний выбор кафедры и преподавателя — чтобы не выбирать заново для каждой недели
function readLast() {
  try { return JSON.parse(localStorage.deptImport || '{}') } catch (e) { return {} }
}

export default {
  name: 'deptRaspImport',
  props: {
    login: String,
    passwordAES: String,
  },
  data() {
    return {
      STATUS,
      departments: [],
      kafId: '',
      weeks: [],
      currentWeek: null,
      week: '',
      teachers: [],
      teacher: '',
      items: null,
      selected: [],
      loading: false,
      importing: false,
      error: '',
    }
  },
  computed: {
    auth() {
      return { login: this.login, passwordAES: this.passwordAES }
    },
    kafOptions() {
      const byFaculty = new Map()
      this.departments.forEach(d => {
        if (!byFaculty.has(d.faculty)) byFaculty.set(d.faculty, [])
        byFaculty.get(d.faculty).push({ value: d.id, text: d.name.replace(/\s*\(ОРУ\)$/, '') })
      })
      return [...byFaculty].map(([label, options]) => ({ label: label.replace(/\s*\(ОРУ\)$/, ''), options }))
    },
    weekOptions() {
      return this.weeks.map(w => ({
        value: w.from,
        text: `${this.short(w.from)} – ${this.short(w.to)}.${w.to.slice(0, 4)}` + (w.from === this.currentWeek ? ' (текущая)' : ''),
      }))
    },
    newKeys() {
      return (this.items || []).filter(i => i.status === 'new').map(i => i.key)
    },
    counts() {
      const c = { new: 0, exists: 0, collision: 0 }
      ;(this.items || []).forEach(i => { c[i.status]++ })
      return c
    },
  },
  async mounted() {
    const last = readLast()
    this.loading = true
    try {
      const { data } = await this.axios.post(PATH + '/api/subgroups/departments', this.auth)
      this.departments = data
      if (last.kaf_id && data.some(d => d.id === last.kaf_id)) {
        this.kafId = last.kaf_id
        await this.loadWeeks()
      }
    } catch (e) {
      this.error = this.errText(e, 'Не удалось загрузить список кафедр с сайта ВятГУ')
    } finally {
      this.loading = false
    }
  },
  methods: {
    errText(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.error) || fallback
    },
    short(day) {
      return day.split('-').reverse().slice(0, 2).join('.')
    },
    dayLabel(day) {
      const [y, m, d] = day.split('-')
      return `${WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()]} ${d}.${m}`
    },
    plural(n) {
      const n10 = n % 10, n100 = n % 100
      if (n10 === 1 && n100 !== 11) return 'занятие'
      if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return 'занятия'
      return 'занятий'
    },
    remember() {
      try { localStorage.deptImport = JSON.stringify({ kaf_id: this.kafId, teacher: this.teacher }) } catch (e) { /* не запоминаем */ }
    },
    async kafChanged() {
      this.teacher = ''
      this.remember()
      await this.loadWeeks()
    },
    async loadWeeks() {
      this.weeks = []
      this.week = ''
      this.teachers = []
      this.items = null
      this.error = ''
      this.loading = true
      try {
        const { data } = await this.axios.post(PATH + '/api/deptrasp/weeks', { ...this.auth, kaf_id: this.kafId })
        this.weeks = data.weeks
        this.currentWeek = data.current
      } catch (e) {
        this.error = this.errText(e, 'Не удалось загрузить недели кафедры')
      } finally {
        this.loading = false
      }
    },
    async weekChanged() {
      this.items = null
      this.error = ''
      this.loading = true
      try {
        const { data } = await this.axios.post(PATH + '/api/deptrasp/teachers', { ...this.auth, kaf_id: this.kafId, week: this.week })
        this.teachers = data.teachers
        // тот же преподаватель, что и в прошлый раз, — сразу показываем занятия
        const last = this.teacher || readLast().teacher
        this.teacher = last && this.teachers.includes(last) ? last : ''
      } catch (e) {
        this.error = this.errText(e, 'Не удалось загрузить расписание кафедры')
      } finally {
        this.loading = false
      }
      if (this.teacher) await this.preview()
    },
    async preview() {
      if (!this.teacher) return
      this.remember()
      this.items = null
      this.error = ''
      this.loading = true
      try {
        const { data } = await this.axios.post(PATH + '/api/deptrasp/preview', { ...this.auth, kaf_id: this.kafId, week: this.week, teacher: this.teacher })
        this.items = data.items
        this.selected = this.newKeys.slice()
      } catch (e) {
        this.error = this.errText(e, 'Не удалось получить занятия')
      } finally {
        this.loading = false
      }
    },
    toggleAll() {
      this.selected = this.selected.length === this.newKeys.length ? [] : this.newKeys.slice()
    },
    async doImport() {
      if (this.counts.collision) {
        const ok = await this.$bvModal.msgBoxConfirm(
          `Коллизий: ${this.counts.collision} — в эти пары у вас уже стоит другое занятие, оно не изменится. Добавить ${this.selected.length} ${this.plural(this.selected.length)} в свободные пары?`,
          { title: 'Коллизии в расписании', okTitle: 'Добавить', cancelTitle: 'Отмена', centered: true, size: 'sm' })
        if (!ok) return
      }
      this.importing = true
      this.error = ''
      try {
        const { data } = await this.axios.post(PATH + '/api/deptrasp/import', {
          ...this.auth, kaf_id: this.kafId, week: this.week, teacher: this.teacher, keys: this.selected,
        })
        let msg = `Добавлено занятий: ${data.added}.`
        if (data.skipped) msg += ` Пропущено (пара уже занята): ${data.skipped}.`
        this.$root.$bvToast.toast(msg, { title: 'Расписание кафедры', variant: 'success', solid: true, autoHideDelay: 6000 })
        this.$emit('imported')
        await this.preview()
      } catch (e) {
        this.error = this.errText(e, 'Не удалось добавить занятия')
      } finally {
        this.importing = false
      }
    },
  },
}
</script>

<style scoped>
.dri-help {
  color: var(--text-muted);
  font-size: 14px;
}
.dri-fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
@media (max-width: 767.98px) {
  .dri-fields {
    grid-template-columns: 1fr;
  }
}
.dri-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
}
.dri-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 14px 0 8px;
}
.dri-badge {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 13px;
  border: 1px solid var(--border);
}
.dri-new { color: var(--success); border-color: var(--success); }
.dri-exists { color: var(--text-muted); }
.dri-collision { color: var(--danger); border-color: var(--danger); font-weight: 600; }
.dri-collision-alert {
  font-size: 14px;
}
.dri-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 50vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.dri-item {
  display: flex;
  gap: 10px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border);
}
.dri-item:last-child {
  border-bottom: 0;
}
.dri-check {
  flex: 0 0 24px;
  margin: 2px 0 0;
}
.dri-check input {
  width: 20px;
  height: 20px;
  accent-color: var(--primary);
}
.dri-body {
  min-width: 0;
}
.dri-when {
  font-size: 13px;
  color: var(--text-muted);
}
.dri-status {
  margin-left: 6px;
  font-weight: 600;
}
.dri-st-new .dri-status { color: var(--success); }
.dri-st-exists { opacity: .6; }
.dri-st-collision {
  background: var(--color-busy-bg);
}
.dri-st-collision .dri-status { color: var(--danger); }
.dri-meta {
  font-size: 13px;
}
.dri-sub {
  color: var(--primary);
}
.dri-local {
  font-size: 13px;
  color: var(--danger);
}
.dri-actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
</style>
